import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight, ArrowLeft, Check } from "lucide-react";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatError, WHATSAPP_LINK } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useBookAbandonment, markBookSubmitted } from "@/hooks/useBookAbandonment";

const COUNTRIES = ["India","Canada","United States","United Kingdom","Australia","United Arab Emirates","Saudi Arabia","Other"];
const CONDITIONS = ["Aortic Aneurysm","Peripheral Artery Disease","Deep Vein Thrombosis","Varicose Veins","Dialysis Access","Uterine Fibroids","Interventional Oncology","Not sure — need guidance","Other"];
const DAYS = ["Mon–Fri (weekdays)","Weekend (Sat)","Any"];
const TIMES = ["Morning (09:00–12:00 IST)","Afternoon (12:00–16:00 IST)","Evening (16:00–19:00 IST)","Any"];
const PLATFORMS = ["WhatsApp video","Google Meet","Zoom","Any (team will pick)"];
const URGENCIES = ["Routine — anytime in the next 2 weeks", "Soon — within this week", "This is urgent — within 24 hours"];

const STEPS = ["About you", "When & how", "Consent"];

export default function Book() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: "India", condition: "",
    preferred_day: "Mon–Fri (weekdays)", preferred_time: "Morning (09:00–12:00 IST)",
    platform: "WhatsApp video", urgency: "Routine — anytime in the next 2 weeks",
    message: "", consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Track abandonment (fires on unload/unmount if email+phone entered but no submit)
  useBookAbandonment(form, step);

  const validateStep = () => {
    if (step === 0) {
      if (!form.name || !form.email || !form.phone) { toast.error("Please fill in your name, email, and phone."); return false; }
      if (!form.condition) { toast.error("Please choose a concern (or 'Not sure')."); return false; }
    }
    if (step === 2) {
      if (!form.consent) { toast.error("Please confirm consent to be contacted."); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const urgencyTag = form.urgency.startsWith("This is urgent") ? "urgent" : "elective";
      await api.post("/booking", { ...form, source: "booking", urgency: urgencyTag });
      trackEvent("booking_submitted", { country: form.country, urgency: urgencyTag });
      markBookSubmitted();
      setDone(true);
      toast.success("Booking request received. The team will reach out to confirm your slot.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="book-page" className="pt-28 md:pt-36 pb-28 min-h-screen">
      <SEO
        title="Book a video consult with Dr Sidhu · Dr Tegbir Vascular"
        description="Book a video consultation with Dr Tegbir Sidhu. Choose your preferred day, time and platform — the team confirms within one working hour."
      />
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <FadeUp>
          <Breadcrumbs trail={[{ to: "/", label: "Home" }, { label: "Book" }]} />
          <div className="mt-8 kbd">— Book a video consult</div>
          <h1 className="mt-6 font-serif text-6xl md:text-[7vw] leading-[0.96] tracking-tightest">
            <MaskedReveal text={"Choose a"} />
            <MaskedReveal text={"window."} delay={0.1} className="italic font-normal" />
            <MaskedReveal text={"We confirm the slot."} delay={0.2} />
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-ink-muted leading-relaxed">
            Tell us your preferred day-block and platform. A coordinator confirms the exact slot within one working hour and sends the join link to your email.
          </p>
        </FadeUp>

        {/* Stepper */}
        <FadeUp className="mt-14">
          <div className="hairline-t pt-6 grid grid-cols-3 gap-4">
            {STEPS.map((s, i) => (
              <div key={s} className={`flex items-center gap-3 pt-3 border-t-2 transition-colors ${i <= step ? "border-crimson" : "border-black/10"}`}>
                <span className={`kbd ${i <= step ? "text-ink" : "text-ink-muted"}`}>0{i+1}</span>
                <span className={`text-sm ${i <= step ? "text-ink" : "text-ink-muted"}`}>{s}</span>
              </div>
            ))}
          </div>
        </FadeUp>

        {/* Content */}
        <FadeUp className="mt-12">
          <div className="hairline p-6 md:p-10 bg-white/60 backdrop-blur">
            {done ? (
              <div data-testid="book-success" className="py-16 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-forest text-bone flex items-center justify-center"><Check size={20}/></div>
                <div className="mt-6 kbd">— Received</div>
                <h3 className="mt-4 font-serif text-4xl md:text-6xl tracking-tightest">Your slot request is in.</h3>
                <p className="mt-6 text-ink-muted max-w-lg mx-auto">
                  The team will confirm your exact slot within one working hour and send the join link to <span className="text-ink font-medium">{form.email}</span>. If you have imaging or reports, WhatsApp them now — it speeds pre-screening.
                </p>
                <div className="mt-10 flex flex-wrap gap-3 justify-center">
                  <a target="_blank" rel="noreferrer" href={WHATSAPP_LINK(`Hello, I just booked a video consult (${form.name}). I can share my imaging here.`)} data-testid="book-success-whatsapp" className="btn-pill-inv">Send imaging on WhatsApp <ArrowUpRight size={14}/></a>
                  <Link to="/" data-testid="book-success-home" className="btn-pill">Back to home</Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="book-form">
                <AnimatePresence mode="wait">
                  {step === 0 && (
                    <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="grid grid-cols-2 gap-6">
                      <div className="col-span-2 md:col-span-1"><Label className="kbd">Name</Label><Input data-testid="book-name" required value={form.name} onChange={(e)=>update("name", e.target.value)} className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0"/></div>
                      <div className="col-span-2 md:col-span-1"><Label className="kbd">Email</Label><Input data-testid="book-email" type="email" required value={form.email} onChange={(e)=>update("email", e.target.value)} className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0"/></div>
                      <div className="col-span-2 md:col-span-1"><Label className="kbd">Phone / WhatsApp</Label><Input data-testid="book-phone" required value={form.phone} onChange={(e)=>update("phone", e.target.value)} className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0" placeholder="+1 · +44 · +91 …"/></div>
                      <div className="col-span-2 md:col-span-1">
                        <Label className="kbd">Country</Label>
                        <Select value={form.country} onValueChange={(v)=>update("country", v)}>
                          <SelectTrigger data-testid="book-country" className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus:ring-0 text-lg px-0"><SelectValue/></SelectTrigger>
                          <SelectContent>{COUNTRIES.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="kbd">What would you like to discuss?</Label>
                        <Select value={form.condition} onValueChange={(v)=>update("condition", v)}>
                          <SelectTrigger data-testid="book-condition" className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus:ring-0 text-lg px-0"><SelectValue placeholder="Choose one — or 'Not sure'"/></SelectTrigger>
                          <SelectContent>{CONDITIONS.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </motion.div>
                  )}

                  {step === 1 && (
                    <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="space-y-8">
                      <div>
                        <Label className="kbd">Urgency</Label>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                          {URGENCIES.map((u) => (
                            <button type="button" key={u} onClick={()=>update("urgency", u)} data-testid={`book-urgency-${URGENCIES.indexOf(u)}`} className={`text-left p-4 hairline transition-colors ${form.urgency===u?"bg-ink text-bone":"bg-white/60 hover:bg-white"}`}>
                              <div className="text-sm">{u}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label className="kbd">Preferred day-block</Label>
                          <Select value={form.preferred_day} onValueChange={(v)=>update("preferred_day", v)}>
                            <SelectTrigger data-testid="book-day" className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus:ring-0 text-lg px-0"><SelectValue/></SelectTrigger>
                            <SelectContent>{DAYS.map((d)=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="kbd">Preferred time</Label>
                          <Select value={form.preferred_time} onValueChange={(v)=>update("preferred_time", v)}>
                            <SelectTrigger data-testid="book-time" className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus:ring-0 text-lg px-0"><SelectValue/></SelectTrigger>
                            <SelectContent>{TIMES.map((t)=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="kbd">Preferred platform</Label>
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                          {PLATFORMS.map((p) => (
                            <button type="button" key={p} onClick={()=>update("platform", p)} data-testid={`book-platform-${p.split(" ")[0].toLowerCase()}`} className={`text-left p-3 hairline text-sm transition-colors ${form.platform===p?"bg-ink text-bone":"bg-white/60 hover:bg-white"}`}>
                              {p}
                            </button>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="space-y-8">
                      <div>
                        <Label className="kbd">Anything else the doctor should know?</Label>
                        <Textarea data-testid="book-message" rows={4} value={form.message} onChange={(e)=>update("message", e.target.value)} className="mt-2 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0 resize-none" placeholder="Existing diagnosis, imaging you've had, medications, family history…"/>
                      </div>
                      <div className="hairline bg-bone p-5 text-sm space-y-2 text-ink-muted">
                        <div className="kbd">Your summary</div>
                        <div className="text-ink"><span className="kbd">Name</span> {form.name} · <span className="kbd">Email</span> {form.email} · <span className="kbd">Country</span> {form.country}</div>
                        <div className="text-ink"><span className="kbd">Concern</span> {form.condition}</div>
                        <div className="text-ink"><span className="kbd">When</span> {form.preferred_day}, {form.preferred_time}</div>
                        <div className="text-ink"><span className="kbd">Platform</span> {form.platform} · <span className="kbd">Urgency</span> {form.urgency}</div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Checkbox data-testid="book-consent" checked={form.consent} onCheckedChange={(v)=>update("consent", Boolean(v))} className="mt-1"/>
                        <Label className="text-sm text-ink-muted leading-relaxed">
                          I consent to being contacted by Dr Sidhu's team by phone, WhatsApp, or email to confirm my consultation, in accordance with the privacy policy. I understand this is a request, not a confirmed booking.
                        </Label>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Navigation */}
                <div className="mt-10 pt-6 border-t border-black/10 flex flex-wrap items-center justify-between gap-4">
                  <div className="kbd">Step {step + 1} of {STEPS.length}</div>
                  <div className="flex gap-3">
                    {step > 0 && (
                      <button type="button" data-testid="book-back" onClick={back} className="btn-pill">
                        <ArrowLeft size={14}/> Back
                      </button>
                    )}
                    {step < STEPS.length - 1 ? (
                      <button type="button" data-testid="book-next" onClick={next} className="btn-pill-inv">
                        Continue <ArrowRight size={14}/>
                      </button>
                    ) : (
                      <button type="submit" data-testid="book-submit" disabled={submitting} className="btn-pill-inv disabled:opacity-60">
                        {submitting ? "Sending…" : "Request slot"} <ArrowUpRight size={14}/>
                      </button>
                    )}
                  </div>
                </div>
              </form>
            )}
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
